/**
 * Translations in progress.
 *
 * §25 names fifteen launch languages. Korean and English are complete; these
 * catalogs cover the safety-critical subset — the §11 progressive-preview
 * status line, the §34 error messages and their actions, and the §13 notice
 * that the original file is protected. Those are the strings a user must
 * understand to avoid losing work, so they come first.
 *
 * Anything not translated here falls back to English at runtime, visibly and by
 * design (§25). `npm run i18n:coverage` reports the exact gap per locale, and
 * `docs/06-i18n-architecture.md` records that these await professional review
 * before launch — machine-assisted wording is not good enough for messages that
 * tell someone whether their file was damaged (§19).
 */
import type { PartialMessageCatalog } from './en.js';

export const ja: PartialMessageCatalog = {
  'status.savedAs': '新しいファイルとして保存しました',
  'status.extracted': '書庫から取り出して保存しました',
  'error.saveFailed': 'ファイルを保存できませんでした。',
  'app.name': 'モドゥビューア',
  'status.checkingFile': 'ファイルを確認しています',
  'status.preparingPreview': '安全なプレビューを準備しています',
  'status.firstPageReady': '最初のページを表示できます',
  'status.loadingRemaining': '残りの内容を読み込んでいます',
  'status.readyToEdit': '編集できます',
  'status.limitedSupport': 'このファイルは一部のみ対応しています',
  'status.mayBeDamaged': 'ファイルが破損しているか不完全な可能性があります',
  'status.originalUnchanged': '元のファイルは変更されていません',
  'error.unableToRead': 'このファイルを読み取れません。',
  'error.damagedOrIncomplete': 'ファイルが破損しているか不完全な可能性があります。',
  'error.passwordProtected': 'このファイルはパスワードで保護されています。',
  'error.featuresUnsupported': 'このファイルの一部の機能には対応していません。',
  'error.permissionExpired': 'ファイルへのアクセス権限の有効期限が切れました。',
  'error.reopenFromSource': '元のアプリからファイルをもう一度開いてください。',
  'error.notEnoughStorage': '保存容量が足りません。',
  'error.previewFailed': 'プレビューを作成できませんでした。',
  'error.editsSavedTemporarily': '編集内容を一時保存しました。',
  'error.originalUnchanged': '元のファイルは変更されていません。',
  'error.mustSaveAsNewFile': '編集したコピーは新しいファイルとして保存する必要があります。',
  'error.formattingMayNotBePreserved': '一部の高度な書式は保持されない場合があります。',
  'error.formulaNotRecalculated': 'この数式は現在再計算できません。',
  'error.conversionRequired': 'この文書は編集する前に変換が必要です。',
  'action.tryAgain': '再試行',
  'action.saveOriginal': '元のファイルを保存',
  'action.openInAnotherApp': '別のアプリで開く',
  'action.recover': '復元',
  'action.saveAsNewFile': '新しいファイルとして保存',
  'action.reportError': 'エラーを報告',
  'action.close': '閉じる',
  'action.cancel': 'キャンセル',
  'action.edit': '編集',
  'edit.originalProtected': '元のファイルは変更されません。編集内容は新しいファイルとして保存されます。',
  'detect.warning.macrosBlocked': 'このファイルにはマクロが含まれています。ファイル内に保持しますが実行はしません。',
  'privacy.localOnly': 'このファイルはすべてこの端末で処理されました。',
};

export const zhHans: PartialMessageCatalog = {
  'status.savedAs': '已另存为新文件',
  'status.extracted': '已从压缩包中取出并保存',
  'error.saveFailed': '无法保存文件。',
  'app.name': '模都阅读器',
  'status.checkingFile': '正在检查文件',
  'status.preparingPreview': '正在准备安全预览',
  'status.firstPageReady': '第一页已就绪',
  'status.loadingRemaining': '正在加载其余内容',
  'status.readyToEdit': '可以编辑了',
  'status.limitedSupport': '此文件仅部分受支持',
  'status.mayBeDamaged': '文件可能已损坏或不完整',
  'status.originalUnchanged': '原文件未被更改',
  'error.unableToRead': '无法读取此文件。',
  'error.damagedOrIncomplete': '文件可能已损坏或不完整。',
  'error.passwordProtected': '此文件受密码保护。',
  'error.featuresUnsupported': '此文件的部分功能不受支持。',
  'error.permissionExpired': '文件访问权限已过期。',
  'error.reopenFromSource': '请从原应用重新打开该文件。',
  'error.notEnoughStorage': '存储空间不足。',
  'error.previewFailed': '无法生成预览。',
  'error.editsSavedTemporarily': '您的编辑已临时保存。',
  'error.originalUnchanged': '原文件未被更改。',
  'error.mustSaveAsNewFile': '编辑后的副本必须另存为新文件。',
  'error.formattingMayNotBePreserved': '部分高级格式可能无法保留。',
  'error.formulaNotRecalculated': '当前无法重新计算此公式。',
  'error.conversionRequired': '此文档需要先转换才能编辑。',
  'action.tryAgain': '重试',
  'action.saveOriginal': '保存原文件',
  'action.openInAnotherApp': '用其他应用打开',
  'action.recover': '恢复',
  'action.saveAsNewFile': '另存为新文件',
  'action.reportError': '报告错误',
  'action.close': '关闭',
  'action.cancel': '取消',
  'action.edit': '编辑',
  'edit.originalProtected': '原文件不会被更改。您的编辑将另存为新文件。',
  'detect.warning.macrosBlocked': '此文件包含宏。宏会保留在文件中，但绝不运行。',
  'privacy.localOnly': '此文件完全在本设备上处理。',
};

export const zhHant: PartialMessageCatalog = {
  'status.savedAs': '已另存為新檔案',
  'status.extracted': '已從壓縮檔中取出並儲存',
  'error.saveFailed': '無法儲存檔案。',
  'app.name': '模都閱讀器',
  'status.checkingFile': '正在檢查檔案',
  'status.preparingPreview': '正在準備安全預覽',
  'status.firstPageReady': '第一頁已就緒',
  'status.loadingRemaining': '正在載入其餘內容',
  'status.readyToEdit': '可以編輯了',
  'status.limitedSupport': '此檔案僅部分支援',
  'status.mayBeDamaged': '檔案可能已損毀或不完整',
  'status.originalUnchanged': '原始檔案未被變更',
  'error.unableToRead': '無法讀取此檔案。',
  'error.damagedOrIncomplete': '檔案可能已損毀或不完整。',
  'error.passwordProtected': '此檔案受密碼保護。',
  'error.featuresUnsupported': '此檔案的部分功能不支援。',
  'error.permissionExpired': '檔案存取權限已過期。',
  'error.reopenFromSource': '請從原應用程式重新開啟該檔案。',
  'error.notEnoughStorage': '儲存空間不足。',
  'error.previewFailed': '無法產生預覽。',
  'error.editsSavedTemporarily': '您的編輯已暫時儲存。',
  'error.originalUnchanged': '原始檔案未被變更。',
  'error.mustSaveAsNewFile': '編輯後的副本必須另存為新檔案。',
  'error.formattingMayNotBePreserved': '部分進階格式可能無法保留。',
  'error.formulaNotRecalculated': '目前無法重新計算此公式。',
  'error.conversionRequired': '此文件需要先轉換才能編輯。',
  'action.tryAgain': '重試',
  'action.saveOriginal': '儲存原始檔案',
  'action.openInAnotherApp': '用其他應用程式開啟',
  'action.recover': '復原',
  'action.saveAsNewFile': '另存為新檔案',
  'action.reportError': '回報錯誤',
  'action.close': '關閉',
  'action.cancel': '取消',
  'action.edit': '編輯',
  'edit.originalProtected': '原始檔案不會被變更。您的編輯將另存為新檔案。',
  'detect.warning.macrosBlocked': '此檔案含有巨集。巨集會保留在檔案中，但絕不執行。',
  'privacy.localOnly': '此檔案完全在本裝置上處理。',
};

export const es: PartialMessageCatalog = {
  'status.savedAs': 'Guardado como archivo nuevo',
  'status.extracted': 'Extraído del archivo comprimido y guardado',
  'error.saveFailed': 'No se pudo guardar el archivo.',
  'status.checkingFile': 'Comprobando el archivo',
  'status.preparingPreview': 'Preparando una vista previa segura',
  'status.firstPageReady': 'La primera página está lista',
  'status.loadingRemaining': 'Cargando el resto del contenido',
  'status.readyToEdit': 'Listo para editar',
  'status.limitedSupport': 'Este archivo tiene compatibilidad limitada',
  'status.mayBeDamaged': 'El archivo puede estar dañado o incompleto',
  'status.originalUnchanged': 'El archivo original no ha cambiado',
  'error.unableToRead': 'No se puede leer este archivo.',
  'error.damagedOrIncomplete': 'El archivo puede estar dañado o incompleto.',
  'error.passwordProtected': 'Este archivo está protegido con contraseña.',
  'error.featuresUnsupported': 'Algunas funciones de este archivo no son compatibles.',
  'error.permissionExpired': 'El permiso de acceso al archivo ha caducado.',
  'error.reopenFromSource': 'Vuelve a abrir el archivo desde la aplicación original.',
  'error.notEnoughStorage': 'No hay suficiente espacio de almacenamiento.',
  'error.previewFailed': 'No se pudo generar la vista previa.',
  'error.editsSavedTemporarily': 'Tus cambios se guardaron temporalmente.',
  'error.originalUnchanged': 'El archivo original no ha cambiado.',
  'error.mustSaveAsNewFile': 'La copia editada debe guardarse como un archivo nuevo.',
  'error.formattingMayNotBePreserved': 'Es posible que no se conserve parte del formato avanzado.',
  'error.formulaNotRecalculated': 'Esta fórmula no se puede recalcular por ahora.',
  'error.conversionRequired': 'Este documento requiere conversión antes de editarlo.',
  'action.tryAgain': 'Reintentar',
  'action.saveOriginal': 'Guardar original',
  'action.openInAnotherApp': 'Abrir en otra aplicación',
  'action.recover': 'Recuperar',
  'action.saveAsNewFile': 'Guardar como archivo nuevo',
  'action.reportError': 'Informar de un error',
  'action.close': 'Cerrar',
  'action.cancel': 'Cancelar',
  'action.edit': 'Editar',
  'edit.originalProtected': 'El archivo original no se modificará. Tus cambios se guardarán como un archivo nuevo.',
  'detect.warning.macrosBlocked': 'Este archivo contiene macros. Se conservan en el archivo, pero nunca se ejecutan.',
  'privacy.localOnly': 'Este archivo se procesó por completo en este dispositivo.',
};

export const fr: PartialMessageCatalog = {
  'status.savedAs': 'Enregistré dans un nouveau fichier',
  'status.extracted': 'Extrait de l’archive et enregistré',
  'error.saveFailed': 'Impossible d’enregistrer le fichier.',
  'status.checkingFile': 'Vérification du fichier',
  'status.preparingPreview': 'Préparation d’un aperçu sécurisé',
  'status.firstPageReady': 'La première page est prête',
  'status.loadingRemaining': 'Chargement du reste du contenu',
  'status.readyToEdit': 'Prêt à modifier',
  'status.limitedSupport': 'Ce fichier n’est que partiellement pris en charge',
  'status.mayBeDamaged': 'Le fichier est peut-être endommagé ou incomplet',
  'status.originalUnchanged': 'Le fichier d’origine n’a pas été modifié',
  'error.unableToRead': 'Impossible de lire ce fichier.',
  'error.damagedOrIncomplete': 'Le fichier est peut-être endommagé ou incomplet.',
  'error.passwordProtected': 'Ce fichier est protégé par un mot de passe.',
  'error.featuresUnsupported': 'Certaines fonctionnalités de ce fichier ne sont pas prises en charge.',
  'error.permissionExpired': 'L’autorisation d’accès au fichier a expiré.',
  'error.reopenFromSource': 'Rouvrez le fichier depuis l’application d’origine.',
  'error.notEnoughStorage': 'Espace de stockage insuffisant.',
  'error.previewFailed': 'Échec de la génération de l’aperçu.',
  'error.editsSavedTemporarily': 'Vos modifications ont été enregistrées temporairement.',
  'error.originalUnchanged': 'Le fichier d’origine n’a pas été modifié.',
  'error.mustSaveAsNewFile': 'La copie modifiée doit être enregistrée comme nouveau fichier.',
  'error.formattingMayNotBePreserved': 'Certaines mises en forme avancées risquent de ne pas être conservées.',
  'error.formulaNotRecalculated': 'Cette formule ne peut pas être recalculée pour l’instant.',
  'error.conversionRequired': 'Ce document doit être converti avant modification.',
  'action.tryAgain': 'Réessayer',
  'action.saveOriginal': 'Enregistrer l’original',
  'action.openInAnotherApp': 'Ouvrir dans une autre application',
  'action.recover': 'Récupérer',
  'action.saveAsNewFile': 'Enregistrer comme nouveau fichier',
  'action.reportError': 'Signaler une erreur',
  'action.close': 'Fermer',
  'action.cancel': 'Annuler',
  'action.edit': 'Modifier',
  'edit.originalProtected': 'Le fichier d’origine ne sera pas modifié. Vos modifications seront enregistrées dans un nouveau fichier.',
  'detect.warning.macrosBlocked': 'Ce fichier contient des macros. Elles sont conservées mais ne sont jamais exécutées.',
  'privacy.localOnly': 'Ce fichier a été entièrement traité sur cet appareil.',
};

export const de: PartialMessageCatalog = {
  'status.savedAs': 'Als neue Datei gespeichert',
  'status.extracted': 'Aus dem Archiv entpackt und gespeichert',
  'error.saveFailed': 'Die Datei konnte nicht gespeichert werden.',
  'status.checkingFile': 'Datei wird geprüft',
  'status.preparingPreview': 'Sichere Vorschau wird vorbereitet',
  'status.firstPageReady': 'Die erste Seite ist bereit',
  'status.loadingRemaining': 'Restlicher Inhalt wird geladen',
  'status.readyToEdit': 'Bereit zum Bearbeiten',
  'status.limitedSupport': 'Diese Datei wird nur eingeschränkt unterstützt',
  'status.mayBeDamaged': 'Die Datei ist möglicherweise beschädigt oder unvollständig',
  'status.originalUnchanged': 'Die Originaldatei wurde nicht verändert',
  'error.unableToRead': 'Diese Datei kann nicht gelesen werden.',
  'error.damagedOrIncomplete': 'Die Datei ist möglicherweise beschädigt oder unvollständig.',
  'error.passwordProtected': 'Diese Datei ist passwortgeschützt.',
  'error.featuresUnsupported': 'Einige Funktionen dieser Datei werden nicht unterstützt.',
  'error.permissionExpired': 'Die Zugriffsberechtigung für die Datei ist abgelaufen.',
  'error.reopenFromSource': 'Öffnen Sie die Datei erneut aus der ursprünglichen App.',
  'error.notEnoughStorage': 'Nicht genügend Speicherplatz.',
  'error.previewFailed': 'Die Vorschau konnte nicht erstellt werden.',
  'error.editsSavedTemporarily': 'Ihre Änderungen wurden vorläufig gespeichert.',
  'error.originalUnchanged': 'Die Originaldatei wurde nicht verändert.',
  'error.mustSaveAsNewFile': 'Die bearbeitete Kopie muss als neue Datei gespeichert werden.',
  'error.formattingMayNotBePreserved': 'Einige erweiterte Formatierungen bleiben möglicherweise nicht erhalten.',
  'error.formulaNotRecalculated': 'Diese Formel kann derzeit nicht neu berechnet werden.',
  'error.conversionRequired': 'Dieses Dokument muss vor dem Bearbeiten konvertiert werden.',
  'action.tryAgain': 'Erneut versuchen',
  'action.saveOriginal': 'Original speichern',
  'action.openInAnotherApp': 'In anderer App öffnen',
  'action.recover': 'Wiederherstellen',
  'action.saveAsNewFile': 'Als neue Datei speichern',
  'action.reportError': 'Fehler melden',
  'action.close': 'Schließen',
  'action.cancel': 'Abbrechen',
  'action.edit': 'Bearbeiten',
  'edit.originalProtected': 'Die Originaldatei wird nicht verändert. Ihre Änderungen werden als neue Datei gespeichert.',
  'detect.warning.macrosBlocked': 'Diese Datei enthält Makros. Sie bleiben erhalten, werden aber nie ausgeführt.',
  'privacy.localOnly': 'Diese Datei wurde vollständig auf diesem Gerät verarbeitet.',
};

export const ru: PartialMessageCatalog = {
  'status.savedAs': 'Сохранено как новый файл',
  'status.extracted': 'Извлечено из архива и сохранено',
  'error.saveFailed': 'Не удалось сохранить файл.',
  'status.checkingFile': 'Проверка файла',
  'status.preparingPreview': 'Подготовка безопасного просмотра',
  'status.firstPageReady': 'Первая страница готова',
  'status.loadingRemaining': 'Загрузка остального содержимого',
  'status.readyToEdit': 'Можно редактировать',
  'status.limitedSupport': 'Этот файл поддерживается частично',
  'status.mayBeDamaged': 'Файл может быть повреждён или неполон',
  'status.originalUnchanged': 'Исходный файл не изменён',
  'error.unableToRead': 'Не удалось прочитать этот файл.',
  'error.damagedOrIncomplete': 'Файл может быть повреждён или неполон.',
  'error.passwordProtected': 'Этот файл защищён паролем.',
  'error.featuresUnsupported': 'Некоторые возможности этого файла не поддерживаются.',
  'error.permissionExpired': 'Срок действия доступа к файлу истёк.',
  'error.reopenFromSource': 'Откройте файл заново из исходного приложения.',
  'error.notEnoughStorage': 'Недостаточно места в хранилище.',
  'error.previewFailed': 'Не удалось создать предварительный просмотр.',
  'error.editsSavedTemporarily': 'Ваши изменения сохранены временно.',
  'error.originalUnchanged': 'Исходный файл не изменён.',
  'error.mustSaveAsNewFile': 'Изменённую копию нужно сохранить как новый файл.',
  'error.formattingMayNotBePreserved': 'Часть сложного форматирования может не сохраниться.',
  'error.formulaNotRecalculated': 'Эту формулу сейчас невозможно пересчитать.',
  'error.conversionRequired': 'Перед редактированием этот документ нужно преобразовать.',
  'action.tryAgain': 'Повторить',
  'action.saveOriginal': 'Сохранить оригинал',
  'action.openInAnotherApp': 'Открыть в другом приложении',
  'action.recover': 'Восстановить',
  'action.saveAsNewFile': 'Сохранить как новый файл',
  'action.reportError': 'Сообщить об ошибке',
  'action.close': 'Закрыть',
  'action.cancel': 'Отмена',
  'action.edit': 'Редактировать',
  'edit.originalProtected': 'Исходный файл не будет изменён. Ваши правки сохранятся как новый файл.',
  'detect.warning.macrosBlocked': 'В этом файле есть макросы. Они сохраняются, но никогда не запускаются.',
  'privacy.localOnly': 'Этот файл полностью обработан на этом устройстве.',
};

export const pt: PartialMessageCatalog = {
  'status.savedAs': 'Salvo como um novo arquivo',
  'status.extracted': 'Extraído do arquivo compactado e salvo',
  'error.saveFailed': 'Não foi possível salvar o arquivo.',
  'status.checkingFile': 'Verificando o arquivo',
  'status.preparingPreview': 'Preparando uma pré-visualização segura',
  'status.firstPageReady': 'A primeira página está pronta',
  'status.loadingRemaining': 'Carregando o restante do conteúdo',
  'status.readyToEdit': 'Pronto para editar',
  'status.limitedSupport': 'Este arquivo tem suporte limitado',
  'status.mayBeDamaged': 'O arquivo pode estar danificado ou incompleto',
  'status.originalUnchanged': 'O arquivo original não foi alterado',
  'error.unableToRead': 'Não é possível ler este arquivo.',
  'error.damagedOrIncomplete': 'O arquivo pode estar danificado ou incompleto.',
  'error.passwordProtected': 'Este arquivo está protegido por senha.',
  'error.featuresUnsupported': 'Alguns recursos deste arquivo não são suportados.',
  'error.permissionExpired': 'A permissão de acesso ao arquivo expirou.',
  'error.reopenFromSource': 'Abra o arquivo novamente pelo aplicativo original.',
  'error.notEnoughStorage': 'Não há espaço de armazenamento suficiente.',
  'error.previewFailed': 'Falha ao gerar a pré-visualização.',
  'error.editsSavedTemporarily': 'Suas edições foram salvas temporariamente.',
  'error.originalUnchanged': 'O arquivo original não foi alterado.',
  'error.mustSaveAsNewFile': 'A cópia editada precisa ser salva como um novo arquivo.',
  'error.formattingMayNotBePreserved': 'Parte da formatação avançada pode não ser preservada.',
  'error.formulaNotRecalculated': 'No momento, esta fórmula não pode ser recalculada.',
  'error.conversionRequired': 'Este documento precisa ser convertido antes da edição.',
  'action.tryAgain': 'Tentar novamente',
  'action.saveOriginal': 'Salvar original',
  'action.openInAnotherApp': 'Abrir em outro aplicativo',
  'action.recover': 'Recuperar',
  'action.saveAsNewFile': 'Salvar como novo arquivo',
  'action.reportError': 'Relatar erro',
  'action.close': 'Fechar',
  'action.cancel': 'Cancelar',
  'action.edit': 'Editar',
  'edit.originalProtected': 'O arquivo original não será alterado. Suas edições serão salvas como um novo arquivo.',
  'detect.warning.macrosBlocked': 'Este arquivo contém macros. Elas são mantidas, mas nunca executadas.',
  'privacy.localOnly': 'Este arquivo foi processado inteiramente neste dispositivo.',
};

/** Right-to-left. Layout mirroring is driven by `localeInfo('ar').direction`. */
export const ar: PartialMessageCatalog = {
  'status.savedAs': 'تم الحفظ كملف جديد',
  'status.extracted': 'تم استخراجه من الأرشيف وحفظه',
  'error.saveFailed': 'تعذّر حفظ الملف.',
  'status.checkingFile': 'جارٍ فحص الملف',
  'status.preparingPreview': 'جارٍ تجهيز معاينة آمنة',
  'status.firstPageReady': 'الصفحة الأولى جاهزة',
  'status.loadingRemaining': 'جارٍ تحميل بقية المحتوى',
  'status.readyToEdit': 'جاهز للتحرير',
  'status.limitedSupport': 'هذا الملف مدعوم جزئيًا فقط',
  'status.mayBeDamaged': 'قد يكون الملف تالفًا أو غير مكتمل',
  'status.originalUnchanged': 'لم يتم تغيير الملف الأصلي',
  'error.unableToRead': 'تعذّر قراءة هذا الملف.',
  'error.damagedOrIncomplete': 'قد يكون الملف تالفًا أو غير مكتمل.',
  'error.passwordProtected': 'هذا الملف محمي بكلمة مرور.',
  'error.featuresUnsupported': 'بعض ميزات هذا الملف غير مدعومة.',
  'error.permissionExpired': 'انتهت صلاحية إذن الوصول إلى الملف.',
  'error.reopenFromSource': 'افتح الملف مرة أخرى من التطبيق الأصلي.',
  'error.notEnoughStorage': 'لا توجد مساحة تخزين كافية.',
  'error.previewFailed': 'تعذّر إنشاء المعاينة.',
  'error.editsSavedTemporarily': 'تم حفظ تعديلاتك مؤقتًا.',
  'error.originalUnchanged': 'لم يتم تغيير الملف الأصلي.',
  'error.mustSaveAsNewFile': 'يجب حفظ النسخة المعدّلة كملف جديد.',
  'error.formattingMayNotBePreserved': 'قد لا يتم الحفاظ على بعض التنسيقات المتقدمة.',
  'error.formulaNotRecalculated': 'تعذّر إعادة حساب هذه المعادلة حاليًا.',
  'error.conversionRequired': 'يحتاج هذا المستند إلى تحويل قبل التحرير.',
  'action.tryAgain': 'إعادة المحاولة',
  'action.saveOriginal': 'حفظ الأصل',
  'action.openInAnotherApp': 'فتح في تطبيق آخر',
  'action.recover': 'استعادة',
  'action.saveAsNewFile': 'حفظ كملف جديد',
  'action.reportError': 'الإبلاغ عن خطأ',
  'action.close': 'إغلاق',
  'action.cancel': 'إلغاء',
  'action.edit': 'تحرير',
  'edit.originalProtected': 'لن يتم تغيير الملف الأصلي. سيتم حفظ تعديلاتك كملف جديد.',
  'detect.warning.macrosBlocked': 'يحتوي هذا الملف على وحدات ماكرو. يتم الاحتفاظ بها دون تشغيلها أبدًا.',
  'privacy.localOnly': 'تمت معالجة هذا الملف بالكامل على هذا الجهاز.',
};

/**
 * Not yet translated. Registered so the locale is selectable and the coverage
 * report shows an honest 0%, rather than the language silently not existing.
 */
export const id: PartialMessageCatalog = {};
export const vi: PartialMessageCatalog = {};
export const th: PartialMessageCatalog = {};
export const hi: PartialMessageCatalog = {};
